import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /**
   * The surrounding block whose taps start the video, set only while autoplay is refused.
   *
   * @type {HTMLElement | null}
   */
  #playTarget = null;

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();

    videoElement.addEventListener('playing', this.#disarm);
    videoElement.addEventListener('pause', this.#requestPlay);

    this.#requestPlay();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const { videoElement } = this.refs;

    videoElement?.removeEventListener('playing', this.#disarm);
    videoElement?.removeEventListener('pause', this.#requestPlay);
    this.#disarm();
  }

  /**
   * Asks the video to play and falls back to tap-to-play when the browser refuses.
   *
   * The markup already carries everything mobile autoplay needs (muted, playsinline, autoplay),
   * but iOS Low Power Mode and Android data saver refuse it anyway and paint the native
   * start-playback button over the video. That button sits under the section content layer, so it
   * can never be tapped. Arming the surrounding block instead turns any tap on it - including one
   * on the button that appears to do nothing - into the gesture that starts playback.
   */
  #requestPlay = () => {
    const { videoElement } = this.refs;

    // Reduced motion hides the video, so there is nothing to start.
    if (!videoElement || matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    videoElement.play().catch(this.#arm);
  };

  /**
   * Starts listening for a tap on the surrounding block.
   */
  #arm = () => {
    if (this.#playTarget) return;

    // The video background is nested in the media wrapper of a group block, or in the background
    // layer of a section. Either way the nearest of the two is the block the visitor sees.
    const target = this.closest('.group-block, .section') ?? this.parentElement;

    if (!(target instanceof HTMLElement)) return;

    this.#playTarget = target;
    this.setAttribute('tap-to-play', '');

    // pointerup rather than click: Safari drops delegated clicks that originate on
    // non-interactive descendants, and pointerup still counts as an activating gesture.
    target.addEventListener('pointerup', this.#playFromGesture);
    target.addEventListener('touchend', this.#playFromGesture, { passive: true });
  };

  /**
   * Stops listening for a tap, once the video plays or the component goes away.
   */
  #disarm = () => {
    const target = this.#playTarget;

    if (!target) return;

    this.#playTarget = null;
    this.removeAttribute('tap-to-play');
    target.removeEventListener('pointerup', this.#playFromGesture);
    target.removeEventListener('touchend', this.#playFromGesture);
  };

  /**
   * Plays the video from a visitor gesture. Nothing is prevented or stopped, so links and
   * buttons inside the block keep working on the same tap.
   */
  #playFromGesture = () => {
    this.refs.videoElement?.play().catch(() => {});
  };
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
